import { supabase } from './supabaseClient';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const CHECKOUT_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/create-checkout-session`;

interface CheckoutParams {
  priceId: string;
  mode: 'payment' | 'subscription';
  successUrl: string;
  cancelUrl: string;
  metadata?: Record<string, string>;
}

export interface StripePriceRecord {
  exercicio_id: string;
  titulo: string;
  preco: number;
  price_id: string;
  product_id: string;
  ativo: boolean;
}

export async function createStripeCheckout(params: CheckoutParams): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const response = await fetch(CHECKOUT_FUNCTION_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro ao criar sessão de pagamento' }));
    throw new Error(error.error || 'Erro ao criar sessão de pagamento');
  }

  const data = await response.json();
  return data.url;
}

/** Busca todos os Price IDs do Supabase */
export async function fetchAllPriceIds(): Promise<StripePriceRecord[]> {
  const { data, error } = await supabase
    .from('stripe_price_ids')
    .select('*')
    .eq('ativo', true);
  if (error) throw new Error(error.message);
  return data || [];
}

/** Busca Price ID de um exercício específico do Supabase */
export async function getPriceIdFromSupabase(exercicioId: string): Promise<{ priceId: string; titulo: string }> {
  const { data, error } = await supabase
    .from('stripe_price_ids')
    .select('price_id, ativo, titulo')
    .eq('exercicio_id', exercicioId)
    .maybeSingle();

  if (error) throw new Error(error.message);

  if (!data) {
    throw new Error(
      `Produto "${exercicioId}" ainda não foi configurado no Stripe. ` +
      `Acesse Admin › Configurar Stripe para criar os produtos automaticamente.`
    );
  }

  if (!data.ativo || !data.price_id) {
    throw new Error(
      `Produto "${exercicioId}" está inativo ou sem Price ID. ` +
      `Acesse Admin › Configurar Stripe para reconfigurar.`
    );
  }

  return { priceId: data.price_id, titulo: data.titulo ?? exercicioId };
}

/**
 * Inicia checkout do Stripe para um exercício avulso.
 * Busca o Price ID dinamicamente do Supabase.
 */
export async function checkoutExercicio(exercicioId: string): Promise<string> {
  const { priceId, titulo } = await getPriceIdFromSupabase(exercicioId);
  const url = await createStripeCheckout({
    priceId,
    mode: 'payment',
    successUrl: `${window.location.origin}/obrigado-exercicio?exercicio=${exercicioId}`,
    cancelUrl: `${window.location.origin}/exercicio/${exercicioId}`,
    metadata: { exercicio_id: exercicioId, exercicio_nome: titulo },
  });
  return url;
}
